import type { IntelligenceAttachmentRef } from "@/features/intelligence/api/shapes";
import { formatGroupedNumber } from "@/shared/i18n/format";

export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

export const ATTACHMENT_TIMEOUT_MS = 60_000;

export const ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.webp,.heic";

export type AttachmentFailure = "too-large" | "empty" | "timeout" | "rejected";

export interface PendingAttachment {
  key: string;
  id: string;
  name: string;
  size: number;
  mimeType: string;
  status: "uploading" | "ready" | "failed";
  failure?: AttachmentFailure;
  status_code?: number;
}

let counter = 0;
export function attachmentKey(): string {
  counter += 1;
  return `att-${counter}-${Date.now()}`;
}

export function localRefusal(file: File): AttachmentFailure | null {
  if (file.size === 0) return "empty";
  if (file.size > MAX_ATTACHMENT_BYTES) return "too-large";
  return null;
}

function isReady(a: PendingAttachment): boolean {
  return a.status === "ready" && a.id !== "";
}

export function readyAttachments(
  attachments: readonly PendingAttachment[],
): IntelligenceAttachmentRef[] {
  return attachments.filter(isReady).map((a) => ({
    id: a.id,
    filename: a.name,
    mimeType: a.mimeType,
    size: a.size,
  }));
}

export function readyAttachmentIds(attachments: readonly PendingAttachment[]): string[] {
  return readyAttachments(attachments).map((a) => a.id);
}

export function hasUploadsInFlight(attachments: readonly PendingAttachment[]): boolean {
  return attachments.some((a) => a.status === "uploading");
}

export function dragCarriesFiles(transfer: DataTransfer | null | undefined): boolean {
  if (!transfer) return false;
  return Array.from(transfer.types).includes("Files");
}

export function droppedFiles(transfer: DataTransfer | null | undefined): File[] {
  if (!transfer?.files) return [];
  return Array.from(transfer.files);
}

const SIZE_UNITS = ["B", "kB", "MB", "GB"] as const;

export function formatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${formatGroupedNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: unit === 0 ? 0 : 1,
  })} ${SIZE_UNITS[unit]}`;
}
